import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import {
    UserGroupIcon,
    CalendarIcon,
    ChartBarIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ClockIcon,
    EyeIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { getLevelData, calculate8Level, CBC_LEVELS } from '../../utils/cbcUtils';
import FilterDropdown from '../common/FilterDropdown';
import CompareControls from '../dashboard/CompareControls';
import TeacherAiAssistantDrawer from './TeacherAiAssistantDrawer';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const StatCard = ({ label, value, icon: Icon, badge, color = 'indigo' }) => (
    <div className="bg-white rounded-2xl md:rounded-3xl shadow-sm border border-slate-100 p-3 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5 hover:shadow-md transition-all h-full">
        <div className={`h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-${color}-50 text-${color}-600 flex items-center justify-center shrink-0`}>
            <Icon className="h-5 w-5 md:h-7 md:w-7" />
        </div>
        <div className="min-w-0 flex-1 w-full">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-black truncate">{label}</p>
            <p className="text-lg md:text-3xl font-black text-[#18216D] leading-none mt-1 truncate">{value}</p>
            {badge && <p className="text-[9px] md:text-[10px] font-bold text-[#FFC425] bg-[#FFC425]/10 px-2 py-0.5 rounded-lg mt-1 md:mt-2 inline-block uppercase tracking-tighter truncate max-w-full">{badge}</p>}
        </div>
    </div>
);


const ClassTeacherDashboard = () => {
    const { get, post } = useApi();
    const { showToast, performanceBands } = useAppState();
    const { user } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const gradeId = searchParams.get('grade');

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageAttention, setCurrentPageAttention] = useState(1);
    const [viewMode, setViewMode] = useState('table');
    const [selectedParents, setSelectedParents] = useState({});
    const [selectedTerm, setSelectedTerm] = useState('');
    const [comparisonFilters, setComparisonFilters] = useState({});
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [studentToRemove, setStudentToRemove] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [expandedBands, setExpandedBands] = useState({});
    const itemsPerPage = 5;
    const itemsPerPageAttention = 5;

    // Step 1: Check role (cached 30 min)
    const { data: roleData, isLoading: roleLoading } = useQuery({
        queryKey: ['class-teacher-role', user?.id, gradeId],
        queryFn: () => get(`/api/teacher/class-teacher/check_role/${gradeId ? `?grade_level=${gradeId}` : ''}`),
        staleTime: 30 * 60 * 1000,
        enabled: !!user,
    });
    const isClassTeacher = roleData?.is_class_teacher ?? false;

    // Step 1.5: Fetch terms
    const { data: termsData, isLoading: termsLoading } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
        enabled: isClassTeacher,
    });

    const terms = useMemo(() => termsData || [], [termsData]);
    
    // Set default term if not set
    React.useEffect(() => {
        if (terms.length > 0 && !selectedTerm) {
            const currentTerm = terms.find(t => t.is_current);
            if (currentTerm) {
                setSelectedTerm(currentTerm.id.toString());
            } else {
                setSelectedTerm(terms[0].id.toString());
            }
        }
    }, [terms, selectedTerm]);

    // Step 2: Fetch dashboard data
    const { data: dashData, isLoading: dashLoading } = useQuery({
        queryKey: ['class-teacher-dashboard', user?.id, gradeId, selectedTerm],
        queryFn: async () => {
            const gradePart = gradeId ? `grade_level=${gradeId}` : '';
            const termPart = selectedTerm ? `term_id=${selectedTerm}` : '';
            const queryParams = [gradePart, termPart].filter(Boolean).join('&');
            const queryUrl = queryParams ? `?${queryParams}` : '';
            
            const [statsData, studentsData, eventsData] = await Promise.all([
                get(`/api/teacher/class-teacher/dashboard_stats/${queryUrl}`),
                get(`/api/teacher/class-teacher/student_list/${queryUrl}`),
                get(`/api/teacher/class-teacher/upcoming_events/${queryUrl}`),
            ]);
            return { stats: statsData, students: studentsData, events: eventsData };
        },
        staleTime: 5 * 60 * 1000,
        enabled: isClassTeacher && (!!selectedTerm || selectedTerm === 'all'),
        onError: () => showToast('Failed to load dashboard data', 'error'),
    });

    // Step 3: Fetch performance matrix for the chart (TARGET set)
    const { data: chartTargetData, isLoading: chartTargetLoading } = useQuery({
        queryKey: ['class-performance-matrix-target', user?.id, gradeId, selectedTerm, comparisonFilters.target_term_id, comparisonFilters.target_year_id],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (gradeId) params.append('grade_level', gradeId);
            
            // Priority: Chart-specific "Select Term" > Global Dashboard Term
            if (comparisonFilters.target_term_id) params.append('term_id', comparisonFilters.target_term_id);
            else if (comparisonFilters.target_year_id) params.append('academic_year', comparisonFilters.target_year_id);
            else if (selectedTerm) params.append('term_id', selectedTerm);

            return get(`/api/teacher/class-teacher/performance_matrix/?${params.toString()}`);
        },
        enabled: isClassTeacher && (!!selectedTerm || !!comparisonFilters.target_term_id || !!comparisonFilters.target_year_id),
    });

    // Step 3.1: Fetch comparison matrix data (COMPARE set)
    const { data: chartCompareData, isLoading: chartCompareLoading } = useQuery({
        queryKey: ['class-performance-matrix-compare', user?.id, gradeId, comparisonFilters.compare_term_id, comparisonFilters.compare_year_id],
        queryFn: async () => {
            if (!comparisonFilters.compare_term_id && !comparisonFilters.compare_year_id) return null;
            
            const params = new URLSearchParams();
            if (gradeId) params.append('grade_level', gradeId);
            
            if (comparisonFilters.compare_term_id) params.append('term_id', comparisonFilters.compare_term_id);
            else if (comparisonFilters.compare_year_id) params.append('academic_year', comparisonFilters.compare_year_id);

            return get(`/api/teacher/class-teacher/performance_matrix/?${params.toString()}`);
        },
        enabled: isClassTeacher && (!!comparisonFilters.compare_term_id || !!comparisonFilters.compare_year_id),
    });


    const loading = roleLoading || termsLoading || (isClassTeacher && dashLoading);
    const stats = dashData?.stats ?? null;
    const rawStudents = dashData?.students;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const students = useMemo(() => rawStudents || [], [rawStudents]);
    const events = dashData?.events ?? [];

    const handleRemoveFromClass = useCallback((student) => {
        setStudentToRemove(student);
        setIsRemoveModalOpen(true);
    }, []);

    const confirmRemoveStudent = async () => {
        if (!studentToRemove) return;
        setIsRemoving(true);
        try {
            await post(`/api/teacher/class-teacher/remove_student/${studentToRemove.id}/`);
            showToast(`${studentToRemove.name} removed successfully`, 'success');
            setIsRemoveModalOpen(false);
            setStudentToRemove(null);
            queryClient.invalidateQueries(['class-teacher-dashboard']);
        } catch (error) {
            console.error('Error removing student:', error);
            showToast('Failed to remove student', 'error');
        } finally {
            setIsRemoving(false);
        }
    };

    const { paginatedStudents, totalPages } = useMemo(() => {
        const lowerTerm = searchTerm.toLowerCase();
        const filtered = students.filter(student =>
            student.name.toLowerCase().includes(lowerTerm) ||
            student.student_id.toLowerCase().includes(lowerTerm)
        );
        const pages = Math.ceil(filtered.length / itemsPerPage);
        const paginated = filtered.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
        return { paginatedStudents: paginated, totalPages: pages };
    }, [students, searchTerm, currentPage]);

    const studentsRequiringAttention = useMemo(() => {
        return students.filter(s => s.attendance_percentage < 75 || s.average_performance?.startsWith('BE'));
    }, [students]);

    // Pie Chart Data Logic (Zero duplicate queries — computes purely from existing dashData.students)
    const pieChartData = useMemo(() => {
        const bands = {
            EE: { label: 'Exceeding Expectations', count: 0, subbands: { EE1: 0, EE2: 0, EE3: 0 } },
            ME: { label: 'Meeting Expectations', count: 0, subbands: { ME1: 0, ME2: 0, ME3: 0 } },
            AE: { label: 'Approaching Expectations', count: 0, subbands: { AE1: 0, AE2: 0, AE3: 0 } },
            BE: { label: 'Below Expectations', count: 0, subbands: { BE1: 0, BE2: 0, BE3: 0 } }
        };

        students.forEach(s => {
            const rawLevel = s.performance_level || (typeof s.average_performance === 'string' ? s.average_performance : s.average_performance?.level);
            if (!rawLevel || typeof rawLevel !== 'string') return;
            const bandCode = rawLevel.substring(0, 2);
            if (bands[bandCode]) {
                bands[bandCode].count++;
                if (bands[bandCode].subbands[rawLevel] !== undefined) {
                    bands[bandCode].subbands[rawLevel]++;
                } else {
                    bands[bandCode].subbands[rawLevel] = 1;
                }
            }
        });

        return Object.entries(bands)
            .filter(([_, data]) => data.count > 0)
            .map(([code, data]) => {
                const levelData = CBC_LEVELS.find(l => l.code === code) || CBC_LEVELS[3];
                return {
                    name: code,
                    label: data.label,
                    value: data.count,
                    color: levelData.hex,
                    bgClass: levelData.bg,
                    textClass: levelData.color,
                    borderClass: levelData.border,
                    subbands: data.subbands
                };
            });
    }, [students]);

    const { paginatedAttention, totalPagesAttention } = useMemo(() => {
        const pages = Math.ceil(studentsRequiringAttention.length / itemsPerPageAttention);
        const paginated = studentsRequiringAttention.slice(
            (currentPageAttention - 1) * itemsPerPageAttention,
            currentPageAttention * itemsPerPageAttention
        );
        return { paginatedAttention: paginated, totalPagesAttention: pages };
    }, [studentsRequiringAttention, currentPageAttention]);

    const attentionCount = studentsRequiringAttention.length;

    const isComparisonEnabled = !!(comparisonFilters.compare_term_id || comparisonFilters.compare_year_id);

    // Derive per-subject class averages with robust comparison and merging
    const chartData = useMemo(() => {
        // Collect all unique subjects from BOTH datasets to ensure no data is hidden
        const subjectMap = new Map();
        
        [chartTargetData, chartCompareData].forEach(dataset => {
            if (dataset?.subjects) {
                dataset.subjects.forEach(sub => {
                    if (!subjectMap.has(sub.code)) {
                        subjectMap.set(sub.code, { code: sub.code, name: sub.name });
                    }
                });
            }
        });

        if (subjectMap.size === 0 && !chartTargetLoading && !chartCompareLoading) return [];
        
        return Array.from(subjectMap.values()).map(sub => {
            // 1. Target Period Calculation
            const targetScores = (chartTargetData?.students || [])
                .map(s => s.subject_scores?.[sub.code])
                .filter(sc => sc && sc.percentage !== null)
                .map(sc => sc.percentage);
            const targetAvg = targetScores.length > 0 ? targetScores.reduce((a, b) => a + b, 0) / targetScores.length : 0;

            // 2. Comparison Period Calculation
            const compareScores = (chartCompareData?.students || [])
                .map(s => s.subject_scores?.[sub.code])
                .filter(sc => sc && sc.percentage !== null)
                .map(sc => sc.percentage);
            const compareAvg = compareScores.length > 0 ? compareScores.reduce((a, b) => a + b, 0) / compareScores.length : 0;

            return {
                learning_area: sub.name || sub.code,
                learning_area_code: sub.code,
                class_average: Math.round(targetAvg),
                compare_average: Math.round(compareAvg),
                level: targetAvg > 0 ? calculate8Level(targetAvg, performanceBands) : 'N/A',
                compare_level: compareAvg > 0 ? calculate8Level(compareAvg, performanceBands) : 'N/A',
                isSkeleton: targetAvg === 0 && compareAvg === 0
            };
        });
    }, [chartTargetData, chartCompareData, chartTargetLoading, chartCompareLoading, performanceBands]);

    if (loading) {
        return <LoadingSpinner fullScreen />;
    }

    if (!isClassTeacher) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-[2.5rem] shadow-xl p-12 text-center max-w-md">
                    <div className="h-20 w-20 bg-slate-100 rounded-[1.5rem] flex items-center justify-center mx-auto mb-6">
                        <ExclamationTriangleIcon className="w-10 h-10 text-slate-400" />
                    </div>
                    <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight mb-3">Not a Class Teacher</h2>
                    <p className="text-sm text-slate-500 mb-8">You are not currently assigned as a class teacher. Please contact administration if you believe this is an error.</p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="px-6 py-3 bg-[#18216D] text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-lg"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
                <header className="relative bg-[#18216D] rounded-[2rem] p-6 md:p-8 text-white shadow-2xl shadow-indigo-900/20">
                    <div className="absolute inset-0 overflow-hidden rounded-[2rem] pointer-events-none">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none"></div>
                    </div>
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">Class Teacher Dashboard</p>
                                <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tighter italic leading-none whitespace-nowrap">
                                    {(() => {
                                        const hour = new Date().getHours();
                                        if (hour >= 5 && hour < 12) return 'Good morning';
                                        if (hour >= 12 && hour < 17) return 'Good afternoon';
                                        if (hour >= 17 && hour < 21) return 'Good evening';
                                        return 'Welcome back';
                                    })()}, <span className="text-[#FFC425]">{user?.first_name || 'Teacher'}</span>
                                </h1>
                            </div>
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
                                <p className="text-indigo-100/70 font-bold text-lg">
                                    Your Class: <span className="text-white">{stats?.grade_name || 'Identifying...'}</span>
                                </p>
                            </div>
                            <div className="h-2 w-24 bg-[#FFC425] rounded-full"></div>
                        </div>
                        <div className="flex gap-4">
                            <FilterDropdown
                                value={selectedTerm}
                                onChange={(val) => setSelectedTerm(val)}
                                options={terms?.map(t => ({ name: t.name, value: t.id.toString() })) || []}
                                placeholder={terms?.find(t => t.is_current)?.name || "Current Term"}
                                className="w-36 z-50"
                            />
                        </div>
                    </div>
                </header>

                {/* Stats Grid */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        label="Total Students"
                        value={stats?.total_students || 0}
                        icon={UserGroupIcon}
                        color="indigo"
                    />
                    <StatCard
                        label="Attendance Rate"
                        value={`${stats?.attendance_rate || 0}%`}
                        icon={CheckCircleIcon}
                        badge={stats?.attendance_rate >= 90 ? 'Excellent' : stats?.attendance_rate >= 75 ? 'Good' : 'Needs Attention'}
                        color="emerald"
                    />
                    <StatCard
                        label="Avg Performance"
                        value={stats?.average_performance?.level || 'N/A'}
                        icon={ChartBarIcon}
                        badge={stats?.average_performance?.numeric ? `${stats.average_performance.numeric} Mean Score` : null}
                        color="amber"
                    />
                    <StatCard
                        label="Need Attention"
                        value={attentionCount}
                        icon={ExclamationTriangleIcon}
                        color="rose"
                    />
                </section>

                {/* Main Content Grid */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Student List */}
                    <div className="lg:col-span-2 bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden flex flex-col">
                        <div className="px-6 md:px-8 py-6 border-b border-slate-50 flex flex-col gap-6 bg-slate-50/30">
                            <div>
                                <h2 className="text-xl font-black text-[#18216D] uppercase tracking-tight">Scholar Roster</h2>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Classroom Management</p>
                            </div>
                            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-4">
                                <div className="relative group flex-1 sm:max-w-xs">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                                        <MagnifyingGlassIcon className="w-4 h-4" />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Identification Lookup..."
                                        value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D]"
                                    />
                                </div>
                                <div className="flex flex-1 sm:flex-none gap-3">
                                    <button
                                        onClick={() => navigate('/teacher/class-attendance')}
                                        className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-[#FFC425] text-[#18216D] rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#18216D] hover:text-white transition-all shadow-xl shadow-amber-900/10 flex items-center justify-center gap-2 group/btn active:scale-95 whitespace-nowrap"
                                    >
                                        <ClockIcon className="w-4 h-4 shrink-0" />
                                        Mark Attendance
                                    </button>
                                    <button
                                        onClick={() => navigate('/teacher/class-roster')}
                                        className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-white border border-slate-100 text-[#18216D] rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm flex items-center justify-center gap-2 group/btn active:scale-95 whitespace-nowrap"
                                    >
                                        <UserGroupIcon className="w-4 h-4 shrink-0" />
                                        View Full Roster
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-x-auto custom-scrollbar">
                            <table className="w-full text-left">
                                <thead className="bg-[#F8FAFC]">
                                    <tr>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Scholar</th>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Guardian Info</th>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Attendance</th>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Performance</th>
                                        <th className="px-8 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {paginatedStudents.length > 0 ? (
                                        paginatedStudents.map(student => (
                                            <tr key={student.id} className="hover:bg-slate-50/50 transition-colors group cursor-pointer" onClick={() => navigate(`/teacher/student-detail/${student.id}`, { state: { studentIds: dashData.students.map(s => s.id) } })}>
                                                <td className="px-8 py-5">
                                                    <div className="flex items-center gap-4">
                                                        <div>
                                                            <p className="font-black text-sm text-[#18216D]">{student.name}</p>
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{student.student_id}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <div className="flex flex-col gap-2 min-w-[150px]">
                                                        {student.parents && student.parents.length > 0 ? (
                                                            <>
                                                                {student.parents.length > 1 && (
                                                                    <div className="flex bg-slate-100/50 p-1 rounded-lg w-fit mb-1">
                                                                        {['FATHER', 'MOTHER'].map(rel => {
                                                                            const exists = student.parents.some(p => p.relationship === rel);
                                                                            if (!exists) return null;
                                                                            const isSelected = (selectedParents[student.id] ||
                                                                                (student.parents.find(p => p.relationship === 'FATHER') ? 'FATHER' : 'MOTHER')) === rel;
                                                                            return (
                                                                                <button
                                                                                    key={rel}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setSelectedParents(prev => ({ ...prev, [student.id]: rel }));
                                                                                    }}
                                                                                    className={`px-2 py-0.5 rounded text-[8px] font-black transition-all ${isSelected ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400'}`}
                                                                                >
                                                                                    {rel === 'FATHER' ? 'Father' : 'Mother'}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                {(() => {
                                                                    const preferredRel = selectedParents[student.id] ||
                                                                        (student.parents.find(p => p.relationship === 'FATHER') ? 'FATHER' : 'MOTHER');
                                                                    const parent = student.parents.find(p => p.relationship === preferredRel) || student.parents[0];
                                                                    return (
                                                                        <div className="animate-in fade-in slide-in-from-left-1 duration-300">
                                                                            <p className="text-xs font-black text-[#18216D]">{parent.name}</p>
                                                                            <p className="text-[9px] font-bold text-slate-400 mt-0.5">{parent.email}</p>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </>
                                                        ) : (
                                                            <p className="text-[10px] font-black text-slate-200 uppercase tracking-widest italic">Not Available</p>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-1 max-w-[60px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full ${student.attendance_percentage < 75 ? 'bg-rose-500' : 'bg-emerald-500'}`}
                                                                style={{ width: `${student.attendance_percentage}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className={`text-xs font-black ${student.attendance_percentage < 75 ? 'text-rose-600' : 'text-slate-600'}`}>
                                                            {student.attendance_percentage}% ({student.absent_days || 0})
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <div className="flex flex-col">
                                                        {(() => {
                                                            const levelData = getLevelData(student.average_performance);
                                                            return (
                                                                <span className={`w-fit px-2 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${levelData.color} ${levelData.bg} ${levelData.border}`}>
                                                                    {student.average_performance || 'N/A'}
                                                                </span>
                                                            );
                                                        })()}
                                                        {student.average_numeric > 0 && (
                                                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter mt-1 ml-1">{student.average_numeric} MS</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-8 py-5 text-right">
                                                    <div className="flex items-center justify-end gap-2 transition-opacity">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/teacher/student-detail/${student.id}`, { state: { studentIds: dashData.students.map(s => s.id) } }); }}
                                                            className="p-2.5 bg-white border border-slate-100 text-[#18216D] rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all shadow-sm"
                                                            title="View Deep Insights"
                                                        >
                                                            <EyeIcon className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleRemoveFromClass(student)}
                                                            className="p-2.5 bg-white border border-slate-100 text-slate-300 rounded-xl hover:bg-rose-50 hover:text-rose-600 transition-all shadow-sm"
                                                            title="Remove from Class"
                                                        >
                                                            <TrashIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="py-20 text-center">
                                                <UserGroupIcon className="w-16 h-16 text-slate-100 mx-auto mb-4" />
                                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No matching scholars found in hub</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Bar */}
                        {totalPages > 1 && (
                            <div className="px-8 py-6 bg-[#F8FAFC] border-t border-slate-100 flex items-center justify-between">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                    Page {currentPage} of {totalPages}
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-[#18216D] disabled:opacity-30 transition-all shadow-sm"
                                    >
                                        <ChevronLeftIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-[#18216D] disabled:opacity-30 transition-all shadow-sm"
                                    >
                                        <ChevronRightIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Upcoming Events */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-black text-[#18216D] flex items-center uppercase tracking-tight">
                                <CalendarIcon className="w-6 h-6 mr-3 text-[#FFC425]" />
                                Upcoming Events
                            </h3>
                        </div>
                        <div className="space-y-4">
                            {events.length > 0 ? (
                                events.map(event => (
                                    <div key={event.id} className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-md transition-all">
                                        <div className="flex items-start gap-3">
                                            <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                                                <CalendarIcon className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-black text-sm text-[#18216D] tracking-tight">{event.title}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <p className="text-[10px] font-black text-[#18216D] uppercase tracking-widest px-2 py-0.5 bg-slate-100 rounded">
                                                        {new Date(event.start_datetime).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                                    </p>
                                                    <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                        {new Date(event.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(event.end_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>
                                                {event.description && (
                                                    <p className="text-xs text-slate-500 mt-2">{event.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-12">
                                    <CalendarIcon className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">No upcoming events</p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* ── Pie Chart + Students Requiring Attention: two-column layout ─── */}
                <section className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                    {/* Grade Band Distribution (Pie Chart) (xl:col-span-2) */}
                    <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-6 flex flex-col">
                        <div className="mb-4">
                            <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tight">Grade Distribution</h3>
                            <p className="text-slate-400 font-bold text-xs mt-0.5 lowercase tracking-widest leading-none">
                                Performance bands distribution
                            </p>
                        </div>

                        {pieChartData.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-8">
                                <ChartBarIcon className="w-10 h-10 text-slate-200 mb-3" />
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest text-center">No performance data</p>
                            </div>
                        ) : (
                            <>
                                <div className="h-44 w-full mb-4 relative">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (!active || !payload?.length) return null;
                                                    const data = payload[0].payload;
                                                    return (
                                                        <div className="bg-white/95 backdrop-blur-md p-3 rounded-2xl shadow-xl border border-slate-100 pointer-events-none">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: data.color }}></div>
                                                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{data.name}</span>
                                                            </div>
                                                            <p className="text-sm font-black text-[#18216D]">{data.value} students</p>
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <Pie
                                                data={pieChartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={50}
                                                outerRadius={75}
                                                paddingAngle={4}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {pieChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none flex-col">
                                        <p className="text-2xl font-black text-[#18216D] leading-none">{pieChartData.reduce((sum, b) => sum + b.value, 0)}</p>
                                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Total</p>
                                    </div>
                                </div>

                                <div className="space-y-2 overflow-y-auto pr-1 flex-1 max-h-56">
                                    {pieChartData.map((band) => (
                                        <div key={band.name} className={`rounded-xl border ${band.borderClass} overflow-hidden transition-all duration-300`}>
                                            <button
                                                onClick={() => setExpandedBands(prev => ({ ...prev, [band.name]: !prev[band.name] }))}
                                                className={`w-full flex items-center justify-between p-3 ${band.bgClass} hover:bg-opacity-80 transition-all`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: band.color }}></div>
                                                    <div className="text-left">
                                                        <p className={`text-[10px] font-black uppercase tracking-widest ${band.textClass}`}>{band.name}</p>
                                                        <p className="text-[8px] font-bold text-slate-500 tracking-tighter truncate max-w-[120px]">{band.label}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-xs font-black ${band.textClass}`}>{band.value}</span>
                                                    {expandedBands[band.name] ?
                                                        <ChevronUpIcon className={`w-3 h-3 ${band.textClass}`} /> :
                                                        <ChevronDownIcon className={`w-3 h-3 ${band.textClass}`} />
                                                    }
                                                </div>
                                            </button>

                                            {/* Sub-bands accordion */}
                                            <div
                                                className={`transition-all duration-300 ease-in-out ${expandedBands[band.name] ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
                                            >
                                                <div className="p-3 pt-0 bg-white grid grid-cols-2 gap-2">
                                                    {Object.entries(band.subbands).filter(([_, count]) => count > 0).sort((a, b) => a[0].localeCompare(b[0])).map(([subcode, count]) => (
                                                        <div key={subcode} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-100">
                                                            <span className="text-[10px] font-black text-[#18216D] uppercase">{subcode}</span>
                                                            <span className="text-[10px] font-bold text-slate-500">{count}</span>
                                                        </div>
                                                    ))}
                                                    {Object.values(band.subbands).every(count => count === 0) && (
                                                        <div className="col-span-2 text-center py-2 text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                                            No specific breakdown
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Students Requiring Attention (xl:col-span-3) */}
                    <div className="xl:col-span-3 bg-white rounded-2xl shadow-sm border border-rose-100 p-6 flex flex-col">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                            <div className="flex items-center gap-3">
                                <ExclamationTriangleIcon className="h-6 w-6 text-rose-500" />
                                <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tight">
                                    Students Requiring Attention
                                </h3>
                            </div>
                            {studentsRequiringAttention.length > 0 && (
                                <div className="flex bg-slate-100 p-1 rounded-xl w-fit">
                                    <button
                                        onClick={() => setViewMode('card')}
                                        className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${viewMode === 'card' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        Cards
                                    </button>
                                    <button
                                        onClick={() => setViewMode('table')}
                                        className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${viewMode === 'table' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        Table
                                    </button>
                                </div>
                            )}
                        </div>

                        {studentsRequiringAttention.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
                                <CheckCircleIcon className="w-12 h-12 text-emerald-400 mb-3" />
                                <h4 className="text-sm font-black text-[#18216D] uppercase tracking-wider">All Students in Good Standing</h4>
                                <p className="text-xs text-slate-400 mt-1 max-w-sm">No students currently require immediate academic or attendance intervention in this stream.</p>
                            </div>
                        ) : viewMode === 'card' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {paginatedAttention.map(student => (
                                    <div key={student.id} className="p-4 bg-rose-50/30 rounded-2xl border border-rose-100 group hover:bg-rose-50 transition-all cursor-pointer" onClick={() => navigate(`/teacher/student-detail/${student.id}`, { state: { studentIds: dashData.students.map(s => s.id) } })}>
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center font-black text-sm">
                                                {student.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="font-black text-sm text-[#18216D] truncate">{student.name}</p>
                                                <p className="text-[10px] text-rose-600 font-bold uppercase tracking-tight">
                                                    {student.attendance_percentage < 75 ? `Low Attendance: ${student.attendance_percentage}%` :
                                                        student.average_performance === 'BE' ? 'Performance Risk (BE Level)' : 'Support Required'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-slate-50 border-b border-rose-50">
                                            <th className="px-4 py-3 text-[9px] font-black text-slate-400 uppercase tracking-widest">Student</th>
                                            <th className="px-4 py-3 text-[9px] font-black text-slate-400 uppercase tracking-widest">ID</th>
                                            <th className="px-4 py-3 text-[9px] font-black text-slate-400 uppercase tracking-widest">Issue</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-rose-50">
                                        {paginatedAttention.map(student => (
                                            <tr key={student.id} className="hover:bg-rose-50/30 transition-colors cursor-pointer" onClick={() => navigate(`/teacher/student-detail/${student.id}`, { state: { studentIds: dashData.students.map(s => s.id) } })}>
                                                <td className="px-4 py-3">
                                                    <span className="text-xs font-black text-[#18216D]">{student.name}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-[10px] font-bold text-slate-500 uppercase">{student.student_id}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-[10px] font-black text-rose-600 uppercase tracking-tight">
                                                        {student.attendance_percentage < 75 ? `Low Attendance: ${student.attendance_percentage}%` :
                                                            student.average_performance === 'BE' ? `Performance Risk (Avg: ${student.average_numeric || 'BE'})` : 'Manual Attention Flag'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination Bar for Attention List */}
                        {totalPagesAttention > 1 && (
                            <div className="px-6 py-4 bg-slate-50/50 border-t border-rose-50 flex items-center justify-between mt-auto pt-4 rounded-xl">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                    Page {currentPageAttention} of {totalPagesAttention}
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPageAttention(p => Math.max(1, p - 1))}
                                        disabled={currentPageAttention === 1}
                                        className="p-2 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-rose-600 disabled:opacity-30 transition-all shadow-sm"
                                    >
                                        <ChevronLeftIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentPageAttention(p => Math.min(totalPagesAttention, p + 1))}
                                        disabled={currentPageAttention === totalPagesAttention}
                                        className="p-2 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-rose-600 disabled:opacity-30 transition-all shadow-sm"
                                    >
                                        <ChevronRightIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </main>

            {/* Performance Comparison Chart Section */}
            <section className="max-w-7xl mx-auto pb-10 px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-sm border border-slate-100">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] mb-1">Class Performance Analytics</p>
                            <h2 className="text-2xl font-black text-[#18216D] tracking-tight">Learning Area Averages</h2>
                            <p className="text-slate-400 font-bold text-sm mt-1">
                                {isComparisonEnabled 
                                    ? "Comparative class-wide performance analysis" 
                                    : `Class-wide mean score — ${terms.find(t => t.id.toString() === (comparisonFilters.target_term_id || selectedTerm))?.name || 'Current Term'}`
                                }
                            </p>
                        </div>
                        <CompareControls onFilterChange={setComparisonFilters} />
                    </div>

                    <div className="flex items-center gap-6 mb-8 px-2">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-[#18216D]"></div>
                            <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                {terms.find(t => t.id.toString() === (comparisonFilters.target_term_id || selectedTerm))?.name || 'Target Period'}
                            </span>
                        </div>
                        {isComparisonEnabled && (
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-[#FFC425]"></div>
                                <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                    {comparisonFilters.compare_term_id 
                                        ? (terms.find(t => t.id.toString() === comparisonFilters.compare_term_id.toString())?.name || 'Comparison Term')
                                        : 'Comparison Year'
                                    }
                                </span>
                            </div>
                        )}
                        <div className="ml-auto flex items-center gap-4">
                             <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded border border-dashed border-slate-300"></div>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">Skeleton (No Data)</span>
                            </div>
                        </div>
                    </div>

                    <div className="h-[400px] w-full">
                        {chartTargetLoading || chartCompareLoading ? (
                            <div className="h-full w-full flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18216D]"></div>
                            </div>
                        ) : chartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 10, right: 20, left: 10, bottom: 90 }}
                                    barGap={8}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis
                                        dataKey="learning_area"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 9, fontWeight: 700 }}
                                        angle={-40}
                                        textAnchor="end"
                                        interval={0}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                        domain={[0, 100]}
                                        tickFormatter={v => `${v}%`}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(24, 33, 109, 0.03)' }}
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length) {
                                                const d = chartData.find(c => c.learning_area === label);
                                                const isComparison = payload.length > 1;
                                                return (
                                                    <div className="bg-white/95 backdrop-blur-md p-4 rounded-3xl shadow-2xl border border-slate-100 min-w-[160px] pointer-events-none">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 border-b border-slate-100 pb-2">{label}</p>
                                                        <div className="space-y-3">
                                                            <div className="flex items-center justify-between gap-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-2 h-2 rounded-full bg-[#18216D]"></div>
                                                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                                                                        {terms.find(t => t.id.toString() === (comparisonFilters.target_term_id || selectedTerm))?.name || 'Target Period'}
                                                                    </p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <span className="text-sm font-black text-[#18216D] italic">{d?.level}</span>
                                                                    <span className="text-[9px] font-bold text-slate-300 ml-1.5">{d?.class_average}%</span>
                                                                </div>
                                                            </div>
                                                            {isComparison && (
                                                                <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-50">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-2 h-2 rounded-full bg-[#FFC425]"></div>
                                                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                                                                            {comparisonFilters.compare_term_id 
                                                                                ? (terms.find(t => t.id.toString() === comparisonFilters.compare_term_id.toString())?.name || 'Comparison')
                                                                                : 'Comparison Year'
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <span className="text-sm font-black text-[#18216D] italic">{d?.compare_level}</span>
                                                                        <span className="text-[9px] font-bold text-slate-300 ml-1.5">{d?.compare_average}%</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar
                                        name="Target Period"
                                        dataKey="class_average"
                                        fill="#18216D"
                                        radius={[4, 4, 0, 0]}
                                        barSize={isComparisonEnabled ? 16 : 32}
                                        background={{ fill: '#f8fafc', radius: [4, 4, 0, 0] }}
                                    />
                                    {isComparisonEnabled && (
                                        <Bar
                                            name="Comparison Period"
                                            dataKey="compare_average"
                                            fill="#FFC425"
                                            radius={[4, 4, 0, 0]}
                                            barSize={16}
                                        />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full w-full flex flex-col items-center justify-center gap-4 text-slate-300">
                                <ChartBarIcon className="w-16 h-16" />
                                <p className="text-[10px] font-black uppercase tracking-[0.2em]">No assessment data recorded for this term yet</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Remove Confirmation Modal */}
            {isRemoveModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-lg font-black text-[#18216D] uppercase tracking-tight flex items-center gap-2">
                                <ExclamationTriangleIcon className="w-6 h-6 text-rose-500" />
                                Confirm Removal
                            </h3>
                            <button
                                onClick={() => { setIsRemoveModalOpen(false); setStudentToRemove(null); }}
                                className="p-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                                disabled={isRemoving}
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-sm font-bold text-slate-600 mb-4">
                                You are about to remove <span className="text-[#18216D]">{studentToRemove?.name}</span> from this stream.
                            </p>
                            <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 flex gap-3">
                                <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 shrink-0" />
                                <p className="text-xs font-black text-rose-800">
                                    This student will be removed from the stream and all associated learning areas. Are you sure you want to proceed?
                                </p>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
                            <button
                                onClick={() => { setIsRemoveModalOpen(false); setStudentToRemove(null); }}
                                disabled={isRemoving}
                                className="px-5 py-2.5 rounded-xl text-xs font-black text-slate-500 hover:bg-white border border-transparent hover:border-slate-200 transition-all uppercase tracking-widest disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmRemoveStudent}
                                disabled={isRemoving}
                                className="px-5 py-2.5 rounded-xl text-xs font-black text-white bg-rose-600 hover:bg-rose-700 shadow-md shadow-rose-600/20 transition-all uppercase tracking-widest disabled:opacity-50 flex items-center gap-2"
                            >
                                {isRemoving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Removing...
                                    </>
                                ) : (
                                    <>
                                        <TrashIcon className="w-4 h-4" />
                                        Confirm Remove
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Assistant Floating Drawer */}
            <TeacherAiAssistantDrawer
                dashboardType="class_teacher"
                termId={selectedTerm}
                gradeName={stats?.grade_name || roleData?.grade_name || "Classroom"}
                gradeId={roleData?.grade_id || gradeId}
                streamId={roleData?.stream_id}
                courseName="Class Teacher Assistant"
                activeTab="class_teacher_dashboard"
            />
        </div>
    );
};

export default ClassTeacherDashboard;
